import Utils from "../../../../../../../../../core/services/utils/utils";

const SMART_DOUBLE_QUOTES = /[\u201C\u201D\u201E\u2033\u2036]/g;
const SMART_SINGLE_QUOTES = /[\u2018\u2019\u201A\u2032]/g;
const ZERO_WIDTH_CHARS = /[\u200B-\u200D\uFEFF]/g;

/**
 * Coerces CSV/parser values to a plain string without scientific notation for integer-like numbers.
 * @param {unknown} raw
 * @returns {string}
 */
const coerceSpreadsheetCellToString = (raw) => {
    if (raw == null) {
        return '';
    }
    if (typeof raw === 'number') {
        if (!Number.isFinite(raw)) {
            return '';
        }
        if (Number.isInteger(raw)) {
            return String(raw);
        }
        const rounded = Math.round(raw);
        if (Math.abs(raw - rounded) < Number.EPSILON) {
            return String(rounded);
        }
        return String(raw);
    }
    return raw.toString();
};

/**
 * Strips common spreadsheet export artifacts so identifiers stay plain strings.
 * Handles e.g. `="PART-001"`, `'PART-001`, `"PART-001"`, smart quotes, zero-width chars.
 *
 * @param {unknown} raw
 * @returns {string}
 */
export const normalizeSpreadsheetCellValue = (raw) => {
    let value = Utils.repairWindows1252Controls(coerceSpreadsheetCellToString(raw));
    if (!value) {
        return '';
    }

    value = value
        .replace(ZERO_WIDTH_CHARS, '')
        .replace(SMART_DOUBLE_QUOTES, '"')
        .replace(SMART_SINGLE_QUOTES, "'")
        .trim();

    if (!value) {
        return '';
    }

    const formulaQuotedMatch = value.match(/^=\s*(["'])([\s\S]*)\1\s*$/);
    if (formulaQuotedMatch) {
        return formulaQuotedMatch[2].trim();
    }

    if (value.startsWith('=')) {
        value = value.slice(1).trim();
    }

    if (
        (value.startsWith('"') && value.endsWith('"') && value.length >= 2) ||
        (value.startsWith("'") && value.endsWith("'") && value.length >= 2)
    ) {
        value = value.slice(1, -1).trim();
    }

    if (value.startsWith("'") && value.length > 1) {
        value = value.slice(1).trim();
    }

    return value.trim();
};

/**
 * Normalizes a part-number cell for import lookup and API payloads (uppercase key).
 * @param {unknown} raw
 * @returns {string}
 */
export const toPartNumberUpper = (raw) => {
    const normalized = normalizeSpreadsheetCellValue(raw);
    return normalized ? normalized.toUpperCase() : '';
};

/** Column-mapper `format` hook for part-number fields. */
export const formatPartNumberImportField = (raw) => toPartNumberUpper(raw);

/**
 * Normalizes an import code for lookup/API (uppercase key after spreadsheet cleanup).
 * Use for customer, vendor, warehouse, territory, and account-manager codes.
 * @param {unknown} raw
 * @returns {string}
 */
export const toImportCodeUpper = (raw) => {
    const normalized = normalizeSpreadsheetCellValue(raw);
    return normalized ? normalized.toUpperCase() : '';
};

/** Column-mapper `format` hook for uppercase import codes (customer, vendor, warehouse, …). */
export const formatImportCodeUpperField = (raw) => toImportCodeUpper(raw);

/** Column-mapper `format` hook that preserves casing after spreadsheet cleanup. */
export const formatSpreadsheetImportField = (raw) => normalizeSpreadsheetCellValue(raw);
